import React, { useEffect, useState, useMemo } from 'react';
import { useAppContext } from '../../contexts/AppContext';
import { Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { RbacTreeDiagram, RbacNode } from '../../components/admin/RbacTreeDiagram';

import { CUSTOMER_ROLE_THEMES, transformRbacToHierarchy } from '../../utils/rbacUtils';

// Customer-specific RBAC structure
const CUSTOMER_RBAC_STRUCTURE = {
    roles: {
        'clientAdmin': ['users:manage', 'projects:manage', 'settings:edit', 'reports:generate', 'team:invite'],
        'projectManager': ['project:read', 'project:edit', 'fmea:approve', 'team:manage', 'reports:view'],
        'engineer': ['fmea:read', 'fmea:write', 'controls:manage', 'dvpr:manage', 'bom:edit', 'pbs:edit'],
        'viewer': ['fmea:read', 'project:read', 'reports:view', 'bom:read']
    }
};

const CustomerRbacVisualizer: React.FC = () => {
    const { currentUser } = useAppContext();
    const [selectedNode, setSelectedNode] = useState<RbacNode | null>(null);
    const [collapsedRoles, setCollapsedRoles] = useState<Set<string>>(new Set());
    const navigate = useNavigate();

    // Initialize with first 2 roles expanded (collapse the rest)
    useEffect(() => {
        const roleNames = Object.keys(CUSTOMER_RBAC_STRUCTURE.roles);
        if (roleNames.length > 2) {
            setCollapsedRoles(new Set(roleNames.slice(2)));
        }
    }, []);

    // Build hierarchy data
    // Build hierarchy data
    const hierarchyData = useMemo<RbacNode | null>(() => {
        const orgName = currentUser?.organization || 'Your Organization';
        return transformRbacToHierarchy(CUSTOMER_RBAC_STRUCTURE.roles, collapsedRoles, `${orgName} RBAC`);
    }, [currentUser, collapsedRoles]);

    const toggleRole = (roleId: string) => {
        setCollapsedRoles(prev => {
            const next = new Set(prev);
            if (next.has(roleId)) {
                next.delete(roleId);
            } else {
                next.add(roleId);
            }
            return next;
        });
    };

    return (
        <div className="relative w-full h-full overflow-hidden">
            {/* Title Badge */}
            <div className="absolute top-4 left-4 z-20 bg-slate-800/90 backdrop-blur-sm px-4 py-2 rounded-xl border border-slate-600 shadow-lg">
                <span className="text-sm font-semibold text-slate-200">Team Access Architecture</span>
            </div>

            <RbacTreeDiagram
                rootNode={hierarchyData}
                roleThemes={CUSTOMER_ROLE_THEMES}
                onRoleClick={setSelectedNode}
                onToggleCollapse={toggleRole}
                selectedNodeId={selectedNode?.id}
                initialTranslate={{ x: 80, y: 200 }}
                backgroundGradient="linear-gradient(135deg, #0f172a 0%, #1e293b 100%)"
                glowFilterId="customer-rbac-glow"
            />

            {/* Selected Node Panel - Matching Customer Styling */}
            {selectedNode && selectedNode.type === 'role' && (
                <div className="absolute bottom-4 right-4 w-72 bg-slate-800/95 backdrop-blur-sm border border-slate-600 rounded-xl p-4 shadow-2xl z-30 animate-in slide-in-from-right-5 fade-in duration-200">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                            <Users className="text-emerald-400" size={20} />
                        </div>
                        <div>
                            <h4 className="font-semibold text-white">{selectedNode.name}</h4>
                            <p className="text-xs text-slate-400">Team Role</p>
                        </div>
                    </div>
                    <div className="text-xs text-slate-400 mb-3">
                        <span className="font-medium text-slate-300">Permissions:</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                            {CUSTOMER_RBAC_STRUCTURE.roles[selectedNode.roleId as keyof typeof CUSTOMER_RBAC_STRUCTURE.roles]?.map((p: string) => (
                                <span key={p} className="px-1.5 py-0.5 bg-slate-700 text-slate-300 rounded text-xs">{p}</span>
                            ))}
                        </div>
                    </div>
                    <button
                        onClick={() => navigate('/admin/permissions')}
                        className="w-full py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium rounded-lg transition-colors"
                    >
                        Configure Role
                    </button>
                </div>
            )}
        </div>
    );
};

export default CustomerRbacVisualizer;
